import { useMemo, useState } from "react";
import { Reactable } from "../../components/ui/reactable";
import { ReportPageShell } from "../../components/ui/reportPageShell";
import { SegmentSwitch } from "../../components/ui/segmentSwitch";
import { SHORT_DUTY_REPORT } from "../../constants/api";
import { formatTimeToAMPM } from "../../components/common/CommonFunctions";
import { cn } from "../../utils/tailwindUtils";

/**
 * Month-wise Short-Duty Review Report.
 * Detail view = one row per missing-punch day; Summary view = per-employee totals.
 * The `view` param is sent with both the data fetch (filter) and export (printFilter),
 * and toggling it flips `serverTrigger` to force a refetch.
 */
const ShortDutyReport = () => {
    const [data, setData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [view, setView] = useState("detail");

    const viewFilter = useMemo(() => ({ view }), [view]);

    const EmployeeCell = ({ row }) => (
        <div>
            <div className="font-medium text-gray-900 dark:text-white">{row?.employee_name || "-"}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">{row?.employee_code || "-"}</div>
        </div>
    );

    const detailColumns = [
        { accessor: "employee_name", Header: "Employee", fixed: true, filterable: true, maxWidth: "260px", Cell: EmployeeCell },
        { accessor: "department", Header: "Department", filterable: true, Cell: ({ value }) => value ?? "-" },
        { accessor: "date", Header: "Date", Cell: ({ value }) => value ?? "-" },
        { accessor: "check_in", Header: "Check-in", Cell: ({ value }) => (value ? formatTimeToAMPM(value) : "-") },
        {
            accessor: "penalty", Header: "Penalty", Cell: ({ value }) => (
                <span className={cn("inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium",
                    value === "Fine"
                        ? "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300"
                        : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300")}>
                    {value || "-"}
                </span>
            )
        },
        { accessor: "value", Header: "Value", Cell: ({ value }) => value ?? "-" },
        {
            accessor: "reviewed", Header: "Status", Cell: ({ value }) => (
                <span className={cn("inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium",
                    value === "Reviewed"
                        ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                        : value?.includes("Suggested")
                            ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                            : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300")}>
                    {value || "-"}
                </span>
            )
        },
        { accessor: "reason", Header: "Reason", Cell: ({ value }) => value ?? "-" },
    ];

    const summaryColumns = [
        { accessor: "employee_name", Header: "Employee", fixed: true, filterable: true, maxWidth: "260px", Cell: EmployeeCell },
        { accessor: "department", Header: "Department", filterable: true, Cell: ({ value }) => value ?? "-" },
        { accessor: "month", Header: "Month", Cell: ({ value }) => value ?? "-" },
        { accessor: "short_duty_days", Header: "Short-duty Days", Cell: ({ value }) => value ?? 0 },
        { accessor: "reviewed_days", Header: "Reviewed", Cell: ({ value }) => value ?? 0 },
        { accessor: "pending_days", Header: "Pending", Cell: ({ value }) => value ?? 0 },
        { accessor: "deduction_days", Header: "Deduction Days", Cell: ({ value }) => value ?? 0 },
        {
            accessor: "fine_total", Header: "Fine Total", Cell: ({ value }) => (
                <span className="font-medium text-rose-600 dark:text-rose-400">₹{Number(value || 0).toLocaleString("en-IN")}</span>
            )
        },
    ];

    return (
        <ReportPageShell
            title="Short-Duty Review Report"
            count={totalRecords}
            headerExtra={(
                <SegmentSwitch
                    options={[
                        { value: "detail", label: "Detail" },
                        { value: "summary", label: "Summary" },
                    ]}
                    value={view}
                    onChange={setView}
                />
            )}
        >
            <p className="mb-3 text-xs text-gray-500 dark:text-gray-400 shrink-0">
                Pending rows include suggested amounts from Payroll Processing defaults until HR finalizes them in Short-Duty Review.
            </p>

            <Reactable
                columns={view === "summary" ? summaryColumns : detailColumns}
                data={data}
                setData={setData}
                setTotalCount={setTotalRecords}
                url={SHORT_DUTY_REPORT}
                serverSide={true}
                serverTrigger={view}
                filter={viewFilter}
                printFilter={viewFilter}
                rowSize="sm"
                isCSVRequired={true}
                isPDFRequired={true}
                isMonthYearRequired={true}
            />
        </ReportPageShell>
    );
};

export default ShortDutyReport;
